import React from 'react';
import moment from 'moment-timezone';
import { Check, Info, Loader2, Undo2, UserCheck } from 'lucide-react';

import { cn } from '../../../lib/utils';
import ButtonName from './ButtonName';
import { BTN_DARK, History, LIFT, LINK } from './VaGovStep';
import { YesNo } from './ReadinessChecklist';

// Verify POA (Nic 2026-10-27): some veterans still have a VSO, claims agent
// or attorney holding power of attorney, and it has to come off before we
// file. The specialist asks on the call and marks Yes / No. On Yes the
// client's dashboard shows the steps and the form (and they get one email);
// the specialist confirms it's removed, or sends it back. Same look as the
// VA.gov step: a short history of what happened, then the one thing to do.
// Every write is a checklist answer: poa_needed yes / no, poa_removed yes
// (confirmed) / no (sent back), null to undo.

const day = (at) => (at ? moment(at).format('MMM D') : null);

// Where the step stands, from the answers first (they show the moment they
// are clicked), then the server's stage for what only it knows (their tap).
export const poaStageOf = (poa = {}, answers = {}) => {
  if (answers.poa_needed === 'no') return 'not_needed';
  if (answers.poa_needed !== 'yes') return 'unasked';
  if (answers.poa_removed === 'yes') return 'confirmed';
  if (answers.poa_removed === 'no') return 'sent_back';
  return poa?.clientDoneAt || poa?.stage === 'client_done' ? 'client_done' : 'needed';
};

// What happened, oldest first. Only real events, like vaGovHistory; the
// answers' dates are the fallback when an event isn't there. Confirmed and
// Sent back follow the stage (an Undo takes them off), their tap and the
// email stay (they did happen).
export const poaHistory = ({ stage, events = [], answerMeta = {}, poa = {} }) => {
  const out = [];
  const of = (type) => (events || []).filter((e) => e.type === type);
  const needed = of('poa_needed');
  needed.forEach((e) =>
    out.push({
      key: `needed-${e.id || e.at}`,
      at: e.at,
      tone: 'quiet',
      text: e.actor ? `${e.actor} marked they have a POA to remove` : 'Has a POA to remove',
    }),
  );
  if (!needed.length && answerMeta.poa_needed?.at) {
    out.push({
      key: 'needed',
      at: answerMeta.poa_needed.at,
      tone: 'quiet',
      text: answerMeta.poa_needed.name ? `${answerMeta.poa_needed.name} marked they have a POA to remove` : 'Has a POA to remove',
    });
  }
  of('poa_email_sent').forEach((e) =>
    out.push({
      key: `email-${e.id || e.at}`,
      at: e.at,
      tone: e.meta?.emailed === false ? 'warn' : 'good',
      text: e.meta?.emailed === false ? "Couldn't email them the steps" : 'Emailed them the steps and the form',
    }),
  );
  const tapped = of('client_poa_done');
  tapped.forEach((e) =>
    out.push({ key: `client-${e.id || e.at}`, at: e.at, tone: 'good', text: "They say it's done (in their portal)" }),
  );
  if (!tapped.length && poa?.clientDoneAt) {
    out.push({ key: 'client', at: poa.clientDoneAt, tone: 'good', text: "They say it's done (in their portal)" });
  }
  const sentBack = of('poa_sent_back');
  sentBack.forEach((e) =>
    out.push({
      key: `back-${e.id || e.at}`,
      at: e.at,
      tone: 'warn',
      text: e.actor ? `${e.actor} sent it back to them` : 'Sent back to them',
    }),
  );
  if (!sentBack.length && stage === 'sent_back') {
    out.push({ key: 'back', at: answerMeta.poa_removed?.at || null, tone: 'warn', text: 'Sent back to them' });
  }
  if (stage === 'confirmed') {
    const m = answerMeta.poa_removed || {};
    out.push({
      key: 'confirmed',
      at: m.at || null,
      tone: 'done',
      text: `${m.name || 'Their specialist'} confirmed it's removed`,
    });
  }
  const t = (x) => (x.at ? new Date(x.at).getTime() : Number.MAX_SAFE_INTEGER);
  return out.sort((a, b) => t(a) - t(b));
};

const PoaStep = ({
  poa,
  step,
  firstName,
  answers = {},
  answerMeta = {},
  events = [],
  canAct,
  saving = false,
  onAnswer,
}) => {
  const who = firstName || 'them';
  const stage = poaStageOf(poa || {}, answers);
  const items = stage === 'unasked' || stage === 'not_needed' ? [] : poaHistory({ stage, events, answerMeta, poa: poa || {} });
  // The email: the latest try says whether they got it.
  const lastEmail = [...(events || [])].filter((e) => e.type === 'poa_email_sent').sort((a, b) => new Date(b.at) - new Date(a.at))[0];
  const emailed = lastEmail ? lastEmail.meta?.emailed !== false : poa?.stage && poa.stage !== 'unasked';
  const undo = (field) => (
    <button type="button" disabled={saving} onClick={() => onAnswer(field, null)} className={cn(LINK, 'ml-auto')}>
      Undo
    </button>
  );
  const confirm = (
    <button type="button" disabled={!canAct || saving} onClick={() => onAnswer('poa_removed', 'yes')} className={BTN_DARK}>
      {saving ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />}
      Confirm it&apos;s removed
    </button>
  );

  return (
    <div className="mt-1.5">
      {step?.need ? (
        <p className="mb-0 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">{step.need}</p>
      ) : null}

      {stage === 'unasked' ? (
        <>
          {step?.ask?.length ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50/70 px-3 py-2 text-xs leading-relaxed text-indigo-900 dark:!bg-indigo-500/10 dark:text-indigo-200">
              <Info size={13} className="mt-0.5 shrink-0 opacity-70" />
              <div className="min-w-0">
                <div className="font-semibold">Ask them:</div>
                <ul className="mb-0 mt-1 list-disc space-y-0.5 pl-4">
                  {step.ask.map((q) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
          <p className="mb-2 mt-3.5 text-[13px] font-medium text-slate-600 dark:text-slate-300">Has a POA to remove?</p>
          <YesNo value={answers.poa_needed} goodIs="no" disabled={!canAct || saving} onChange={(v) => onAnswer('poa_needed', v)} />
        </>
      ) : stage === 'not_needed' ? (
        <div className={cn('mt-[16px] rounded-2xl p-[18px]', LIFT)}>
          <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px]">
            <span className="inline-flex items-center gap-[8px] text-[13.5px] font-semibold text-emerald-700 dark:text-emerald-300">
              <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-emerald-500/15">
                <Check size={13} strokeWidth={3} />
              </span>
              No POA to remove
              {answerMeta.poa_needed?.name ? (
                <span className="font-normal text-slate-500 dark:text-slate-400">
                  · {answerMeta.poa_needed.name}
                  {answerMeta.poa_needed.at ? `, ${day(answerMeta.poa_needed.at)}` : ''}
                </span>
              ) : null}
            </span>
            {canAct ? undo('poa_needed') : null}
          </div>
        </div>
      ) : (
        <div className={cn('mt-[16px] rounded-2xl p-[18px]', LIFT)}>
          {items.length ? (
            <History items={items} />
          ) : (
            <p className="mb-0 text-[13px] italic text-slate-400">Nothing about their POA on record yet.</p>
          )}

          <div className="mt-[16px] border-0 border-t border-solid border-slate-100 pt-[14px] dark:border-white/10">
            {stage === 'confirmed' ? (
              <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px]">
                <span className="inline-flex items-center gap-[8px] text-[13.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                  <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-emerald-500/15">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  Confirmed
                </span>
                {canAct ? undo('poa_removed') : null}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-x-[14px] gap-y-[10px]">
                  {confirm}
                  <span className="text-[12.5px] text-slate-500 dark:text-slate-400">
                    {stage === 'client_done' ? (
                      <>They say it&apos;s done. Confirm it, or send it back.</>
                    ) : stage === 'sent_back' ? (
                      <>
                        Sent back{answerMeta.poa_removed?.at ? ` ${day(answerMeta.poa_removed.at)}` : ''}. You&apos;ll see it here when
                        they tap <ButtonName>I&apos;ve done it</ButtonName>.
                      </>
                    ) : emailed ? (
                      <>Waiting on {who}. Their dashboard shows the steps and the form, and they got the email.</>
                    ) : (
                      <>Waiting on {who}. Their dashboard shows the steps and the form. The email couldn&apos;t go out, so tell them on a call.</>
                    )}
                  </span>
                </div>
                {canAct ? (
                  <div className="mt-[12px] flex flex-wrap items-center gap-x-[14px]">
                    {stage === 'client_done' ? (
                      <button type="button" disabled={saving} onClick={() => onAnswer('poa_removed', 'no')} className={LINK}>
                        <Undo2 size={12} /> Not done? Send it back
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => onAnswer(stage === 'sent_back' ? 'poa_removed' : 'poa_needed', null)}
                        className={LINK}
                      >
                        Undo
                      </button>
                    )}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PoaStep;
